import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { apiErr } from "../lib/api";
import { COUNTRIES } from "../lib/constants";
import Logo from "../components/Logo";
import LanguageSwitcher from "../components/LanguageSwitcher";
import Seo from "../components/Seo";

export default function Register() {
  const [f, setF] = useState({ first_name: "", last_name: "", email: "", phone: "", country: "France", password: "", confirm: "" });
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  // Capture le code partenaire (?ref=) et le mémorise pour l'attribuer à l'inscription.
  const urlRef = searchParams.get("ref");
  if (urlRef) { try { sessionStorage.setItem("aca_ref", urlRef); } catch {} }
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (f.password !== f.confirm) return toast.error(t("rg_mismatch"));
    if (f.password.length < 6) return toast.error(t("rg_short"));
    setLoading(true);
    try {
      const { confirm, ...payload } = f;
      let ref = urlRef;
      if (!ref) { try { ref = sessionStorage.getItem("aca_ref"); } catch {} }
      if (ref) payload.ref = ref;
      await register(payload);
      try { sessionStorage.removeItem("aca_ref"); } catch {}
      toast.success(t("rg_created"));
      nav("/app/deposer");
    } catch (err) {
      toast.error(apiErr(err));
    } finally { setLoading(false); }
  };

  return (
    <div className="grain grid min-h-screen bg-void lg:grid-cols-2">
      <Seo title="Créer un compte" description="Créez gratuitement votre compte APP CHALLENGE ACADEMY et déposez votre première idée d'application dès aujourd'hui." path="/inscription" />
      <div className="absolute right-4 top-4 z-20"><LanguageSwitcher /></div>
      <div className="relative hidden overflow-hidden border-r border-white/10 bg-surface lg:block">
        <div className="pointer-events-none absolute -bottom-20 -right-20 h-96 w-96 rounded-full bg-cyan/15 blur-[120px]" />
        <div className="relative flex h-full flex-col justify-between p-12">
          <Link to="/"><Logo /></Link>
          <div>
            <div className="text-[11px] uppercase tracking-[0.25em] text-cyan">{t("rg_badge")}</div>
            <h2 className="mt-4 font-display text-4xl font-900 leading-tight text-white">
              {t("rg_side_t")}
            </h2>
            <p className="mt-6 max-w-sm text-sm text-ink-secondary">
              {t("rg_side_d")}
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center px-5 py-16 sm:px-12 overflow-y-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
          <Link to="/" className="mb-8 inline-flex items-center gap-1.5 text-sm text-ink-secondary hover:text-white lg:hidden">
            <ArrowLeft size={15} /> {t("back")}
          </Link>
          <h1 className="font-display text-3xl font-700 text-white">{t("rg_title")}</h1>
          <p className="mt-2 text-sm text-ink-secondary">{t("rg_sub")}</p>
          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="register-form">
            <div className="grid grid-cols-2 gap-4">
              <Field label={t("f_first")} value={f.first_name} onChange={set("first_name")} testid="reg-firstname" autoFocus />
              <Field label={t("f_last")} value={f.last_name} onChange={set("last_name")} testid="reg-lastname" />
            </div>
            <Field label={t("f_email")} type="email" value={f.email} onChange={set("email")} testid="reg-email" />
            <div className="grid grid-cols-2 gap-4">
              <Field label={t("f_phone")} value={f.phone} onChange={set("phone")} testid="reg-phone" />
              <label className="block">
                <span className="mb-1.5 block text-[11px] uppercase tracking-[0.15em] text-white">{t("f_country")}</span>
                <select value={f.country} onChange={set("country")} data-testid="reg-country" className="w-full rounded-xl border border-white/15 bg-white/[0.06] px-4 py-3.5 text-sm text-white outline-none focus:border-cyan focus:ring-1 focus:ring-cyan">
                  {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
            </div>
            <Field label={t("f_pwd")} type="password" value={f.password} onChange={set("password")} testid="reg-password" />
            <Field label={t("f_confirm")} type="password" value={f.confirm} onChange={set("confirm")} testid="reg-confirm" />
            <button
              type="submit" disabled={loading} data-testid="register-submit"
              className="group mt-2 inline-flex w-full items-center justify-center gap-2 bg-cyan px-6 py-4 text-sm font-600 uppercase tracking-wide text-void transition-all hover:glow-cyan disabled:opacity-60"
            >
              {loading ? t("rg_submitting") : t("rg_submit")}
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </button>
          </form>
          <p className="mt-6 text-sm text-ink-secondary">
            {t("rg_have")} <Link to="/connexion" data-testid="to-login" className="text-cyan hover:underline">{t("rg_login")}</Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}

function Field({ label, type = "text", value, onChange, testid, autoFocus }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-[11px] uppercase tracking-[0.15em] text-white">{label}</span>
      <input
        required type={type} value={value} onChange={onChange} data-testid={testid} autoFocus={autoFocus}
        className="w-full rounded-xl border border-white/15 bg-white/[0.06] px-4 py-3.5 text-sm text-white outline-none transition-colors focus:border-cyan focus:ring-1 focus:ring-cyan"
      />
    </label>
  );
}
